import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

export default function Terms() {
    return (
        <div className="min-h-screen grain" data-testid="terms-page">
            <div className="max-w-3xl mx-auto px-5 md:px-8 py-10">
                <Link to="/" className="btn-ghost text-sm inline-flex items-center gap-1 mb-6">
                    <ChevronLeft size={14} /> Back home
                </Link>
                <div className="font-script text-xl text-[var(--brand-orange)]">virtually unstoppable</div>
                <h1 className="font-serif text-4xl mt-1 mb-2">Terms of Service</h1>
                <p className="text-xs text-[var(--text-ink)]/55 mb-8">Last updated: July 2026</p>

                <Section title="Acceptance">
                    By creating an account or using Virtually Unstoppable (&ldquo;the Service&rdquo;) you agree to these Terms and to
                    our <Link to="/privacy" className="text-[var(--brand-purple)]">Privacy Policy</Link>.
                </Section>

                <Section title="The Service">
                    <p>Virtually Unstoppable provides AI-assisted productivity tools for professionals and students.</p>
                    <p><strong>Professional</strong> features include client management, projects, meeting recording with
                        AI-generated notes, client memory and productivity tools.</p>
                    <p><strong>Student</strong> features include class organisation, assessments, study hub, lecture
                        transcription, missed lecture mode and AI-powered study assistance.</p>
                    <p>Features may be added, modified or removed over time as we continue improving the Service.</p>
                </Section>

                <Section title="Eligibility &amp; account">
                    You must be at least 16 to use the Service. You are responsible for keeping your password secure and for
                    all activity under your account. You agree to provide accurate information at signup.
                </Section>

                <Section title="Free trial &amp; subscriptions">
                    <p>Every account begins with a <strong>7-day free trial</strong> with access to all available features.</p>
                    <ul>
                        <li><strong>Professional Plan</strong>: £14/month</li>
                        <li><strong>Student Plan</strong>: £5/month</li>
                    </ul>
                    <p>Prices include applicable taxes where required and payments are processed securely through Stripe.</p>
                    <p>Subscriptions renew automatically until cancelled. You may cancel at any time through the Customer
                        Portal. Your subscription will remain active until the end of your current paid billing period.</p>
                    <p>We may occasionally offer promotional pricing. Promotional prices apply only for the advertised period.</p>
                    <p>If subscription prices change in the future, existing subscribers will receive at least 30 days&apos;
                        notice before any new pricing takes effect.</p>
                </Section>

                <Section title="Refunds">
                    <p>Because Virtually Unstoppable provides immediate access to digital services, subscription payments are
                        generally non-refundable except where required by applicable law.</p>
                    <p>If you believe you have been charged incorrectly, please contact <a
                        href="mailto:hello@virtuallyunstoppable.app"
                        className="text-[var(--brand-purple)]">hello@virtuallyunstoppable.app</a> within 14 days of the charge.</p>
                </Section>

                <Section title="Your content">
                    <p>You retain ownership of all content you upload or create within Virtually Unstoppable, including notes,
                        projects, client information, lectures, documents, audio recordings and AI conversations.</p>
                    <p>You grant us a limited licence to securely store, process and display your content solely for the purpose
                        of providing and improving the Service.</p>
                    <p>We do not claim ownership of your content.</p>
                </Section>

                <Section title="AI features &amp; responsible use">
                    <p>Our AI features are designed to support productivity, learning, organisation and drafting.</p>
                    <p>They must not be used to:</p>
                    <ul>
                        <li>complete coursework or assessments intended for grading;</li>
                        <li>generate dissertations, essays or assignments for submission as your own work;</li>
                        <li>circumvent academic integrity policies;</li>
                        <li>impersonate another person or organisation;</li>
                        <li>create, promote or distribute unlawful or harmful content.</li>
                    </ul>
                    <p>AI-generated content may contain inaccuracies, omissions or outdated information. You are responsible
                        for reviewing and verifying all AI-generated outputs before relying on them for academic, professional,
                        legal, financial or medical purposes.</p>
                    <p>You remain solely responsible for how you use AI-generated content. We reserve the right to suspend or
                        terminate accounts that misuse AI features.</p>
                </Section>

                <Section title="Acceptable use">
                    <p>You agree not to: reverse-engineer the Service, abuse rate limits, attempt unauthorised access,
                        upload malware, infringe IP rights, or use the Service to harass others.</p>
                    <p>We may suspend or restrict accounts whose use threatens the security, stability or fair use of the Service.</p>
                </Section>

                <Section title="Beta features">
                    Some features may be labelled <strong>Beta</strong>, <strong>Preview</strong> or <strong>Experimental</strong>.
                    These features are provided for testing purposes and may change, be modified or removed at any time
                    without prior notice.
                </Section>

                <Section title="Service availability">
                    <p>We aim to provide a reliable service but cannot guarantee uninterrupted availability.</p>
                    <p>Maintenance, updates, technical issues or third-party outages may occasionally affect availability.</p>
                    <p>The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; to the fullest extent
                        permitted by law.</p>
                </Section>

                <Section title="Termination">
                    <p>You may delete your account at any time. We may suspend or terminate accounts that violate these
                        Terms or misuse the Service, providing reasonable notice where practical.</p>
                    <p>Following account deletion, we may retain limited information where required by law or for fraud
                        prevention. All remaining personal content will be handled in accordance with our <Link to="/privacy"
                            className="text-[var(--brand-purple)]">Privacy Policy</Link>.</p>
                </Section>

                <Section title="Liability">
                    <p>To the maximum extent permitted by law, our total liability for any claim relating to the Service
                        is limited to the amount you paid us in the 12 months preceding the claim. We are not liable for
                        indirect, incidental or consequential damages.</p>
                    <p>Nothing in these Terms limits any liability that cannot legally be excluded under applicable law.</p>
                </Section>

                <Section title="Force majeure">
                    We are not responsible for delays or failures caused by events beyond our reasonable control, including
                    internet outages, cloud service interruptions, natural disasters, government actions or similar events.
                </Section>

                <Section title="Governing law">
                    These Terms are governed by the laws of England &amp; Wales, except where mandatory consumer protection
                    laws in your country of residence apply.
                </Section>

                <Section title="Changes">
                    We may update these Terms. We&apos;ll post the new version here and, for material changes, email active
                    subscribers at least 14 days before the changes take effect.
                </Section>

                <Section title="Contact">
                    <p>Questions about these Terms can be sent to:</p>
                    <p><a href="mailto:hello@virtuallyunstoppable.app" className="text-[var(--brand-purple)]">hello@virtuallyunstoppable.app</a></p>
                </Section>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="mb-6">
            <h2 className="font-serif text-2xl mb-2">{title}</h2>
            <div className="text-sm text-[var(--text-ink)]/80 space-y-2 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-1">
                {typeof children === "string" ? <p>{children}</p> : children}
            </div>
        </div>
    );
}
